'use client';

import { addressHeroSchema } from '@energy-retailer/schema';
import { deleteCookie, setCookie } from 'cookies-next';
import { useRouter, useSearchParams } from 'next/navigation';
import { useState } from 'react';

import { getPageLink } from '@/app/lib/contentful';
import { FlowSwitchModal } from '@/components/flow-switch-modal';
import { Form } from '@/components/form';
import { MultipleAddressModal } from '@/components/multiple-address-modal';
import { NmiSearchModal } from '@/components/nmi-search-modal';
import {
  INVITE_CODE_COOKIE,
  NMI_SEARCH_ITEM,
  CONTACT_SEARCH_ITEM,
  PROMO_TITLE_COOKIE,
  PROMO_ID_COOKIE,
  PROMO_TERMS_URL_COOKIE,
  MODAL_NAMES,
} from '@/constants';
import { useAddressDetails } from '@/lib/hooks/use-address-details';

import { AddressLookup } from './address-lookup';

import type {
  PromoSettings,
  HeroSearch as Props,
} from '@/app/generated/graphql';
import type { DataType } from '@/components/typeahead/typeahead.types';
import type {
  AddressHeroSchema,
  PropertySchema,
} from '@energy-retailer/schema';
import { useJoinFormStore } from '@/stores/join-form-store/join-form-store';
import JoinFormModalRenderer from '../../join-form-modal-renderer/join-form-modal-renderer';

export const HeroSearchForm = ({
  heading,
  body,
  heroLink,
  heroCtaText,
  promo,
}: Pick<Props, 'heading' | 'body' | 'heroLink' | 'heroCtaText'> & {
  promo?: PromoSettings | null;
}) => {
  const {
    selectAddress,
    resetProperty,
    openModal,
    getCurrentModal,
    closeModal,
    property,
    setPromotionId,
    resetPromotion,
  } = useJoinFormStore();
  const { data, isFetching } = useAddressDetails<PropertySchema>(
    property.address.id
  );
  const currentModal = getCurrentModal();
  const [isFlowSwitchModalOpen, setIsFlowSwitchModalOpen] =
    useState<boolean>(false);
  const router = useRouter();
  const searchParams = useSearchParams();

  const updateCookies = () => {
    resetPromotion();
    deleteCookie(PROMO_ID_COOKIE);
    deleteCookie(PROMO_TITLE_COOKIE);
    deleteCookie(PROMO_TERMS_URL_COOKIE);
    deleteCookie(INVITE_CODE_COOKIE);

    if (promo) {
      const token = searchParams.get('token');

      setPromotionId(promo.promoId ?? '0');
      setCookie(PROMO_ID_COOKIE, promo.promoId ?? '0', {});
      setCookie(PROMO_TITLE_COOKIE, promo.title || '', {});

      if (promo.termsLink) {
        setCookie(
          PROMO_TERMS_URL_COOKIE,
          getPageLink(promo.termsLink).href,
          {}
        );
      }

      if (token) {
        setCookie(INVITE_CODE_COOKIE, token, {});
      }
    }
  };

  const onOptionSelect = async (value: DataType): Promise<void> => {
    if (value.id === CONTACT_SEARCH_ITEM.id) {
      openModal(MODAL_NAMES.GasAddressNotFound);
    } else if (value.id === NMI_SEARCH_ITEM.id) {
      openModal(MODAL_NAMES.NmiSearch);
    } else {
      await selectAddress(value);
    }
  };

  return (
    <>
      <Form<AddressHeroSchema>
        onSubmit={() => {
          updateCookies();
          router.push('/compare-energy-plans');
        }}
        className="w-full"
        schema={addressHeroSchema}
        id="address-lookup-form"
      >
        <AddressLookup
          isLoadingSubmitData={isFetching}
          onOptionSelect={onOptionSelect}
          heading={heading}
          body={body}
          heroLink={heroLink}
          heroCtaText={heroCtaText}
        />

        <JoinFormModalRenderer />

        <FlowSwitchModal
          isModalOpen={isFlowSwitchModalOpen}
          setIsModalOpen={setIsFlowSwitchModalOpen}
          onContinue={() => {
            router.push('/compare-energy-plans');
          }}
          onCancel={() => {
            resetProperty();
          }}
          description="The address you have entered is electricity only and we can see a gas plan in your cart, would you like to remove this from your cart?"
        />
      </Form>

      <MultipleAddressModal
        remainOnPage={true}
        electricityDetails={data}
        isLoading={isFetching}
        isModalOpen={currentModal === MODAL_NAMES.MultipleAddress}
        setIsModalOpen={() => closeModal(MODAL_NAMES.MultipleAddress)}
      />

      <NmiSearchModal
        isModalOpen={currentModal === MODAL_NAMES.NmiSearch}
        setIsModalOpen={() => closeModal(MODAL_NAMES.NmiSearch)}
      />
    </>
  );
};
